import { SwapHoriz, TrendingDown } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { animated, useSpring } from '@react-spring/web';
import type { TrainingPlan, Workout } from '@shared/types';
import { useDrag } from '@use-gesture/react';
import React, { useEffect, useMemo } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useWorkoutTypes } from '../../contexts/WorkoutTypesContext';
import { useDeloadWeek } from '../../hooks/useDeloadWeek';
import { useWeekNavigation } from '../../hooks/useWeekNavigation';
import { useWeekSummary } from '../../hooks/useWeekSummary';
import { useWorkoutSwap } from '../../hooks/useWorkoutSwap';
import apiClient from '../../services/api';
import {
  formatDistance,
  getNextWeek as getNextWeekDate,
  getPreviousWeek as getPreviousWeekDate,
  toISODateString,
} from '../../utils/dateUtils';
import { logger } from '../../utils/logger';
import { getAllWorkouts } from '../../utils/planUtils';
import { loadUserProfileSync } from '../../utils/userProfile';
import { LoadingAnimation } from '../common/LoadingAnimation';
import NoteInfoModal from '../common/NoteInfoModal';
import AICoachButton from '../dashboard/AICoachButton';
import { WeeklyProgressChart } from './WeeklyProgressChart';
import { WeekNavigation } from './WeekNavigation';
import { WeekSummaryCard } from './WeekSummaryCard';
import { WorkoutCard } from './WorkoutCard';

interface TrainingCalendarProps {
  trainingPlan: TrainingPlan | null;
  isLoading: boolean;
  onWorkoutClick: (workout: Workout) => void;
  onTrainingPlanUpdate?: (updatedPlan: TrainingPlan) => void;
  onActionStateChange?: (state: {
    isSwapMode: boolean;
    isDeloadWeek: boolean;
    isTodayInCurrentWeek: boolean;
    onSwapModeToggle: () => void;
    onDeloadWeekClick: () => void;
  }) => void;
  healthStatus?: 'normal' | 'sick' | 'injured';
}

/**
 * Main training calendar component - displays weekly training schedule
 * with workout details, navigation, and deload week management.
 * Features week-by-week navigation and workout swapping functionality.
 */
export const TrainingCalendar: React.FC<TrainingCalendarProps> = ({
  trainingPlan,
  isLoading,
  onWorkoutClick,
  onTrainingPlanUpdate,
  onActionStateChange,
  healthStatus = 'normal',
}) => {
  const { getWorkoutTypeColor } = useWorkoutTypes();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Week navigation state
  const { currentWeek, setCurrentWeek, currentWeekDates } = useWeekNavigation();

  const hasFutureWorkouts = useMemo(() => {
    const allWorkouts = getAllWorkouts(trainingPlan);
    if (allWorkouts.length === 0 || currentWeekDates.length === 0) return false;

    const currentWeekEndKey = toISODateString(currentWeekDates[currentWeekDates.length - 1]);
    return allWorkouts.some(workout => {
      const dateString = workout?.training_date;
      if (!dateString) return false;
      return dateString > currentWeekEndKey;
    });
  }, [trainingPlan, currentWeekDates]);

  const hasPastWorkouts = useMemo(() => {
    const allWorkouts = getAllWorkouts(trainingPlan);
    if (allWorkouts.length === 0 || currentWeekDates.length === 0) return false;

    const currentWeekStartKey = toISODateString(currentWeekDates[0]);
    return allWorkouts.some(workout => {
      const dateString = workout?.training_date;
      if (!dateString) return false;
      return dateString < currentWeekStartKey;
    });
  }, [trainingPlan, currentWeekDates]);

  // Deload week management
  const {
    currentDeloadWeek,
    isDeloadWeek,
    showDeloadWeekDialog,
    setShowDeloadWeekDialog,
    handleChangeDeloadWeek,
    isLoadingDeloadWeek,
  } = useDeloadWeek(trainingPlan, currentWeekDates, onTrainingPlanUpdate);

  // Workout swap functionality
  const {
    isSwapMode,
    selectedWorkout,
    isSwapping,
    swapConfirmOpen,
    workoutToSwap,
    setIsSwapping,
    setSwapConfirmOpen,
    startSwapMode,
    cancelSwapMode,
    selectWorkoutForSwap,
  } = useWorkoutSwap();

  // Notifications
  const { showApiError, showSuccess } = useNotification();

  const canNavigateToPrevious = () => hasPastWorkouts;
  const canNavigateToNext = () => hasFutureWorkouts;

  // Swipe gesture setup - Carousel style
  const SWIPE_VELOCITY_THRESHOLD = 0.4; // velocity threshold for quick swipes

  const [{ x, opacity }, api] = useSpring(() => ({
    x: 0,
    opacity: 1,
    config: { tension: 300, friction: 30 },
  }));

  const bind = useDrag(
    ({ down, movement: [mx], cancel, direction: [xDir], velocity: [vx], last }) => {
      // Prevent navigation if we're at boundaries
      const canGoLeft = canNavigateToPrevious();
      const canGoRight = canNavigateToNext();

      if ((xDir > 0 && !canGoLeft) || (xDir < 0 && !canGoRight)) {
        // Rubberband effect at boundaries - reduced resistance
        const resistance = 3.5;
        api.start({
          x: down ? mx / resistance : 0,
          opacity: 1,
          immediate: down,
          config: { tension: 300, friction: 30 },
        });
        if (last) cancel();
        return;
      }

      // If finger is down, follow the drag with slight opacity fade
      if (down) {
        const opacityValue = 1 - Math.min(Math.abs(mx) / 400, 0.3);
        api.start({ x: mx, opacity: opacityValue, immediate: true });
      } else {
        // Finger lifted - check if we should navigate or spring back
        // Trigger navigation if swiped more than 50% of screen width or fast velocity
        const screenWidth = window.innerWidth;
        const swipePercentage = Math.abs(mx) / screenWidth;
        const shouldNavigate = swipePercentage > 0.5 || vx > SWIPE_VELOCITY_THRESHOLD;

        if (shouldNavigate) {
          // Determine direction from movement, not xDir which can be 0
          const swipeDirection = mx > 0 ? 1 : -1; // positive = right (previous), negative = left (next)
          logger.debug('Navigating week with swipe', {
            mx,
            swipeDirection,
            xDir,
            canGoLeft,
            canGoRight,
          });

          // Carousel slide-out animation
          const slideOutDistance = swipeDirection > 0 ? window.innerWidth : -window.innerWidth;

          // Trigger navigation immediately based on swipe direction
          if (swipeDirection > 0 && canGoLeft) {
            logger.debug('immediately: Previous');
            handlePreviousWeek();
          } else if (swipeDirection < 0 && canGoRight) {
            logger.debug('immediately: Next');
            handleNextWeek();
          }

          // Animate slide-out then slide-in
          api.start({
            x: slideOutDistance,
            opacity: 0,
            config: { tension: 280, friction: 28 },
            onRest: () => {
              // Slide in from opposite side after content changes
              api.start({
                from: { x: -slideOutDistance, opacity: 0 },
                to: { x: 0, opacity: 1 },
                config: { tension: 280, friction: 28 },
              });
            },
          });
        } else {
          // Spring back to center with bounce
          api.start({
            x: 0,
            opacity: 1,
            config: { tension: 300, friction: 26 },
          });
        }
      }
    },
    {
      axis: 'x',
      filterTaps: true,
      rubberband: 0.15,
      from: () => [x.get(), 0],
    }
  );

  // Navigation functions for week controls
  const handlePreviousWeek = () => {
    if (!canNavigateToPrevious()) return;
    setCurrentWeek(previousWeek => getPreviousWeekDate(previousWeek));
  };

  const handleNextWeek = () => {
    logger.debug('handleNextWeek called');
    if (!canNavigateToNext()) return;
    setCurrentWeek(previousWeek => getNextWeekDate(previousWeek));
  };

  // Week summary calculations
  const { weekSummary } = useWeekSummary(trainingPlan, currentWeekDates);

  const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Get the active week dates - the week that contains today
  const getActiveWeekDates = (): Date[] => {
    const today = new Date();
    // Find the week that contains today from all available weeks
    if (allWeeks.length > 0) {
      const weekWithToday = allWeeks.find(week => week.dates.some(date => isSameDay(date, today)));
      if (weekWithToday) {
        return weekWithToday.dates;
      }
    }
    // Fallback to currentWeekDates if not found in allWeeks (mobile view)
    return currentWeekDates;
  };

  const isTodayInCurrentWeek = (): boolean => {
    const today = new Date();
    const activeWeekDates = getActiveWeekDates();
    return activeWeekDates.some(date => isSameDay(date, today));
  };

  const getWorkoutForDate = (date: Date): Workout | null => {
    const allWorkouts = getAllWorkouts(trainingPlan);
    if (allWorkouts.length === 0) return null;
    const dateString = toISODateString(date);
    return allWorkouts.find(workout => workout.training_date === dateString) || null;
  };

  const handleWorkoutSelection = (workout: Workout) => {
    if (isSwapMode) {
      // Allow swapping any workouts (including across weeks) that are not rest days
      if (workout.name.toLowerCase() !== 'rest') {
        selectWorkoutForSwap(workout);
      } else {
        showApiError(new Error('Cannot swap rest days'));
      }
      return;
    }
    onWorkoutClick(workout);
  };

  const handleSwapModeToggle = () => {
    if (isSwapMode) {
      cancelSwapMode();
    } else {
      startSwapMode();
    }
  };

  const handleSwapConfirm = async () => {
    if (!selectedWorkout || !workoutToSwap || !trainingPlan || !onTrainingPlanUpdate) return;

    setIsSwapping(true);
    try {
      const userProfile = loadUserProfileSync();
      if (!userProfile) {
        throw new Error('User profile not found. Please configure your profile first.');
      }

      const response = await apiClient.swapWorkouts(
        userProfile,
        trainingPlan,
        selectedWorkout.index,
        workoutToSwap.index
      );

      if (response.success && response.data) {
        // Store the current week before updating
        const weekBeforeSwap = currentWeek;
        onTrainingPlanUpdate(response.data);
        cancelSwapMode();
        // Ensure we stay on the same week after the swap
        if (currentWeek.getTime() !== weekBeforeSwap.getTime()) {
          logger.debug('Week changed after swap, restoring to:', weekBeforeSwap);
          setCurrentWeek(weekBeforeSwap);
        }
        showSuccess(`Successfully swapped "${selectedWorkout.name}" with "${workoutToSwap.name}"`);
      } else {
        showApiError(new Error(response.message || 'Failed to swap workouts'));
      }
    } catch (error) {
      logger.error('Error swapping workouts', { error });
      showApiError(error instanceof Error ? error : new Error('Unknown error occurred'));
    } finally {
      setIsSwapping(false);
    }
  };

  const handleSwapCancel = () => {
    setSwapConfirmOpen(false);
  };

  // Expose action state to parent component
  useEffect(() => {
    if (onActionStateChange) {
      onActionStateChange({
        isSwapMode,
        isDeloadWeek,
        isTodayInCurrentWeek: isTodayInCurrentWeek(),
        onSwapModeToggle: handleSwapModeToggle,
        onDeloadWeekClick: () => setShowDeloadWeekDialog(true),
      });
    }
  }, [isSwapMode, isDeloadWeek, currentWeekDates, trainingPlan, onActionStateChange]);

  // Group workouts by week for desktop view - must be before early returns
  const allWeeks = useMemo(() => {
    const allWorkouts = getAllWorkouts(trainingPlan);
    if (allWorkouts.length === 0) return [];

    const weeks: Array<{ dates: Date[]; weekNumber: number }> = [];
    const workoutsByWeek = new Map<string, Date[]>();

    allWorkouts.forEach(workout => {
      const date = new Date(workout.training_date);
      const mondayOfWeek = new Date(date);
      const dayOfWeek = date.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday = start of week
      mondayOfWeek.setDate(date.getDate() + diff);
      mondayOfWeek.setHours(0, 0, 0, 0);

      const weekKey = toISODateString(mondayOfWeek);
      if (!workoutsByWeek.has(weekKey)) {
        workoutsByWeek.set(weekKey, []);
      }
    });

    // Create array of all week dates
    Array.from(workoutsByWeek.keys())
      .sort()
      .forEach((weekKey, index) => {
        const monday = new Date(weekKey);
        const dates = Array.from({ length: 7 }, (_, i) => {
          const date = new Date(monday);
          date.setDate(monday.getDate() + i);
          return date;
        });
        weeks.push({ dates, weekNumber: index + 1 });
      });

    return weeks;
  }, [trainingPlan]);

  // Get the selected/viewed week number (1-based) for chart highlighting
  const getSelectedWeekNumber = (): number => {
    if (currentWeekDates.length === 0) return 1;
    const selectedWeek = allWeeks.find(week =>
      week.dates.some(date => currentWeekDates.some(currentDate => isSameDay(date, currentDate)))
    );
    return selectedWeek?.weekNumber || 1;
  };

  // Loading state
  if (isLoading) {
    return (
      <LoadingAnimation
        message="Loading training plan..."
        size={isMobile ? 'small' : 'medium'}
        minHeight="200px"
        ariaLabel="Loading training plan"
      />
    );
  }

  // No training plan state
  if (!trainingPlan) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Training Plan Available
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please generate a training plan first.
        </Typography>
      </Box>
    );
  }

  const canGoPrev = canNavigateToPrevious();
  const canGoNext = canNavigateToNext();

  // Render single week (for mobile carousel)
  const renderWeek = (dates: Date[], _weekNumber?: number, isActiveWeek: boolean = false) => {
    const weekKey = dates.length > 0 ? toISODateString(dates[0]) : '';

    return (
      <Box
        key={weekKey}
        mb={isDesktop ? 0.75 : 2}
        sx={{
          border: isActiveWeek && isDesktop ? 2 : 1,
          borderColor: isActiveWeek && isDesktop ? 'primary.main' : 'divider',
          borderRadius: 1,
          p: isDesktop ? 0.75 : 0,
          bgcolor: isActiveWeek && isDesktop ? 'action.hover' : 'transparent',
          transition: 'all 0.3s ease',
          boxShadow: isActiveWeek && isDesktop ? 2 : 0,
          filter: !isActiveWeek && isDesktop ? 'blur(1px) opacity(0.6)' : 'none',
          '&:hover':
            isDesktop && !isActiveWeek
              ? {
                  filter: 'blur(0px) opacity(0.85)',
                  transition: 'all 0.3s ease',
                }
              : {},
        }}
      >
        <Grid container spacing={{ xs: isMobile ? 1 : 0.5, sm: isDesktop ? 0.5 : 2 }}>
          {dates.map(date => {
            const dateKey = toISODateString(date);
            const workout = getWorkoutForDate(date);
            const isToday = isSameDay(date, new Date());
            const isSelected = selectedWorkout?.index === workout?.index;

            const isSwappable = Boolean(
              isSwapMode && workout && (workout.name || '').toLowerCase() !== 'rest'
            );

            return (
              <Grid size={{ xs: isDesktop ? 12 / 7 : 12 }} key={dateKey}>
                <WorkoutCard
                  date={date}
                  workout={workout}
                  isToday={isToday}
                  isSelected={isSelected}
                  isSwappable={isSwappable}
                  onClick={workout ? () => handleWorkoutSelection(workout) : undefined}
                  getWorkoutTypeColor={(name: string) => getWorkoutTypeColor(name).primary}
                  healthStatus={healthStatus}
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const handleJumpToToday = () => {
    setCurrentWeek(new Date());
  };

  // Check if the currently viewed week contains today (for week navigation button)
  const isCurrentWeekViewed = (): boolean => {
    const today = new Date();
    return currentWeekDates.some(date => isSameDay(date, today));
  };

  return (
    <Box>
      <Box>
        <WeekNavigation
          currentWeek={currentWeek}
          onPreviousWeek={handlePreviousWeek}
          onNextWeek={handleNextWeek}
          onJumpToToday={handleJumpToToday}
          canGoToPrevious={canGoPrev}
          canGoToNext={canGoNext}
          isCurrentWeek={isCurrentWeekViewed()}
          weeklyTSS={weekSummary.totalTSS}
        >
          {/* Plan-level AI Coach note icon */}
          {trainingPlan.planNote && (
            <NoteInfoModal
              title="Training Plan - AI Coach Overview"
              note={trainingPlan.planNote}
              iconSize="small"
              iconColor="primary"
            />
          )}
        </WeekNavigation>
        <WeekSummaryCard weekSummary={weekSummary} />

        {/* Mobile: Single week carousel */}
        {!isDesktop && (
          <animated.div
            {...bind()}
            style={{
              x,
              opacity,
              touchAction: 'pan-y',
              cursor: 'grab',
              willChange: 'transform, opacity',
            }}
          >
            {renderWeek(currentWeekDates)}
          </animated.div>
        )}

        {/* Desktop: All weeks stacked vertically */}
        {isDesktop && (
          <Box>
            {allWeeks.map(week => {
              const isActiveWeek = week.dates.some(date =>
                currentWeekDates.some(currentDate => isSameDay(date, currentDate))
              );
              return renderWeek(week.dates, week.weekNumber, isActiveWeek);
            })}
          </Box>
        )}

        {/* Action buttons - Only show on mobile/tablet (on desktop they're in the sidebar) */}
        <Box
          display={{ xs: 'flex', xl: 'none' }}
          justifyContent="center"
          gap={2}
          sx={{ mt: 3, mb: 2 }}
        >
          <Button
            variant={isSwapMode ? 'contained' : 'outlined'}
            size="medium"
            startIcon={<SwapHoriz />}
            onClick={handleSwapModeToggle}
            sx={{
              minWidth: isMobile ? 'auto' : 200,
              flex: isMobile ? 'none' : 1,
              maxWidth: isMobile ? 'auto' : 220,
              textTransform: 'none',
              ...(isSwapMode
                ? {
                    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                    border: 0,
                    boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)',
                      boxShadow: '0 4px 8px 3px rgba(255, 105, 135, .4)',
                      transform: 'translateY(-1px)',
                    },
                  }
                : {
                    borderColor: '#9C27B0',
                    color: '#9C27B0',
                    background:
                      'linear-gradient(45deg, transparent 0%, rgba(156, 39, 176, 0.05) 100%)',
                    '&:hover': {
                      borderColor: '#7B1FA2',
                      background:
                        'linear-gradient(45deg, rgba(156, 39, 176, 0.1) 0%, rgba(156, 39, 176, 0.15) 100%)',
                      transform: 'translateY(-1px)',
                    },
                  }),
              '&:disabled': {
                borderColor: '#CCCCCC',
                color: '#999999',
                background: 'transparent',
                cursor: 'not-allowed',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {isMobile ? '' : isSwapMode ? '❌ Cancel Swap' : 'Swap workouts'}
          </Button>

          <Button
            variant="outlined"
            size="medium"
            startIcon={<TrendingDown />}
            onClick={() => setShowDeloadWeekDialog(true)}
            disabled={!isDeloadWeek}
            sx={{
              minWidth: isMobile ? 'auto' : 200,
              flex: isMobile ? 'none' : 1,
              maxWidth: isMobile ? 'auto' : 220,
              textTransform: 'none',
              borderColor: isDeloadWeek ? '#FF9800' : '#CCCCCC',
              color: isDeloadWeek ? '#FF9800' : '#999999',
              background: isDeloadWeek
                ? 'linear-gradient(45deg, transparent 0%, rgba(255, 152, 0, 0.05) 100%)'
                : 'transparent',
              '&:hover': {
                borderColor: isDeloadWeek ? '#F57C00' : '#CCCCCC',
                background: isDeloadWeek
                  ? 'linear-gradient(45deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.15) 100%)'
                  : 'transparent',
                transform: isDeloadWeek ? 'translateY(-1px)' : 'none',
              },
              '&:disabled': {
                borderColor: '#CCCCCC',
                color: '#999999',
                background: 'transparent',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {isMobile ? '' : 'Change Deload Week'}
          </Button>

          {/* AI Coach Button */}
          <AICoachButton
            onChangesApplied={() => {
              // Refresh the training plan after AI Coach changes are applied
              if (onTrainingPlanUpdate) {
                // The parent component will refetch the plan
                logger.info('AI Coach changes applied, triggering plan refresh');
              }
            }}
            disabled={isSwapMode}
          />
        </Box>

        {/* Training Progression Chart - at bottom */}
        <WeeklyProgressChart
          trainingPlan={trainingPlan}
          activeWeekNumber={getSelectedWeekNumber()}
        />
      </Box>

      {/* Swap Confirmation Dialog */}
      <Dialog open={swapConfirmOpen} onClose={handleSwapCancel}>
        <DialogTitle>Confirm Workout Swap</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>Are you sure you want to swap these workouts?</Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="primary">
              Workout A (#{selectedWorkout?.index}):
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {selectedWorkout?.name} - {formatDistance(selectedWorkout?.total_distance || 0)}
            </Typography>

            <Typography variant="subtitle2" color="primary">
              Workout B (#{workoutToSwap?.index}):
            </Typography>
            <Typography variant="body2">
              {workoutToSwap?.name} - {formatDistance(workoutToSwap?.total_distance || 0)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSwapCancel} disabled={isSwapping}>
            Cancel
          </Button>
          <Button
            onClick={handleSwapConfirm}
            variant="contained"
            disabled={isSwapping}
            startIcon={isSwapping ? <CircularProgress size={16} /> : <SwapHoriz />}
          >
            {isSwapping ? 'Swapping...' : 'Swap Workouts'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deload Week Dialog */}
      <Dialog open={showDeloadWeekDialog} onClose={() => setShowDeloadWeekDialog(false)}>
        <DialogTitle>Change Deload Week</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Choose which week should be your deload week. This will recalculate your entire 5-week
            training plan.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current deload week: Week {currentDeloadWeek}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            {[1, 2, 3, 4, 5].map(week => (
              <Button
                key={week}
                variant={currentDeloadWeek === week ? 'contained' : 'outlined'}
                onClick={() => handleChangeDeloadWeek(week)}
                disabled={isLoadingDeloadWeek || currentDeloadWeek === week}
                sx={{ minWidth: 60 }}
              >
                Week {week}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeloadWeekDialog(false)} disabled={isLoadingDeloadWeek}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
